import { useState, useEffect, useRef } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getCriptoInfo, getSearchSuggestions } from '../../services/AgentService';
import './Agent.css';

function Agent() {
    const [criptoInput, setCriptoInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);
    const [activeTab, setActiveTab] = useState('market');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const suggestionsRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) && 
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Timeout ref para manter entre renderizações
    const debounceTimeoutRef = useRef(null);

    // Função para buscar sugestões - copiado da pasta agent/script.js
    async function fetchSuggestions(query) {
        try {
            const data = await getSearchSuggestions(query);
            setSuggestions(data || []);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Erro ao buscar sugestões:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }

    // Handler de input usando debounce exatamente como na pasta agent/script.js
    function handleInputChange(e) {
        const value = e.target.value;
        setCriptoInput(value);
        
        // Limpar timeout anterior
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        // Criar novo timeout - exatamente como na pasta agent
        debounceTimeoutRef.current = setTimeout(() => {
            const query = value.trim();
            if (query.length > 1) {
                fetchSuggestions(query);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);
    }

    function handleInputFocus() {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    }

    function handleSuggestionClick(coin) {
        setCriptoInput(coin.name);
        setShowSuggestions(false);
        searchCripto(coin.name);
    }

    async function searchCripto(name = null) {
        const cripto = name || criptoInput.trim();
        if (!cripto) return;

        setShowSuggestions(false);
        setLoading(true);
        setError(null);
        setResults(null);

        try {
            const data = await getCriptoInfo(cripto);
            
            if (data.error) {
                throw new Error(data.error);
            }

            setResults(data);
        } catch (err) {
            console.error('Erro:', err);
            setError(err.response?.data?.error || err.message || 'Falha ao buscar informações. Verifique o nome da criptomoeda e tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    function handleKeyPress(e) {
        if (e.key === 'Enter') {
            searchCripto();
        }
    }

    function formatCurrency(value) {
        if (!value && value !== 0) return 'N/A';
        if (value >= 1e12) {
            return `$${(value / 1e12).toFixed(2)}T`;
        }
        if (value >= 1e9) {
            return `$${(value / 1e9).toFixed(2)}B`;
        }
        if (value >= 1e6) {
            return `$${(value / 1e6).toFixed(2)}M`;
        }
        if (value >= 1e3) {
            return `$${(value / 1e3).toFixed(2)}K`;
        }
        if (value >= 1) {
            return `$${value.toFixed(2)}`;
        }
        if (value >= 0.01) {
            return `$${value.toFixed(4)}`;
        }
        return `$${value.toFixed(6)}`;
    }

    function formatNumber(num, decimals = 2) {
        if (!num && num !== 0) return 'N/A';
        return num.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals
        });
    }

    function getSentimentClass(sentiment) {
        switch(sentiment) {
            case 'muito positivo':
            case 'positivo':
                return 'positive';
            case 'muito negativo':
            case 'negativo':
                return 'negative';
            default:
                return 'neutral';
        }
    }

    function capitalizeFirstLetter(string) {
        if (!string) return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="agent-container">

                    <div className="search-container">
                        <div className="search-box" ref={suggestionsRef}>
                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                            <input
                                ref={inputRef}
                                type="text"
                                className="cripto-input"
                                placeholder="Digite o nome ou símbolo da criptomoeda (ex: Bitcoin, ETH, Solana...)"
                                value={criptoInput}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onKeyPress={handleKeyPress}
                            />
                            <button 
                                className="search-btn" 
                                onClick={() => searchCripto()}
                                disabled={loading}
                            >
                                Analisar
                            </button>
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="suggestions-container">
                                    {suggestions.map((coin, index) => (
                                        <div
                                            key={index}
                                            className="suggestion-item"
                                            onClick={() => handleSuggestionClick(coin)}
                                        >
                                            <span className="suggestion-name">{coin.name}</span>
                                            <span className="suggestion-symbol">{coin.symbol.toUpperCase()}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Buscando dados em tempo real...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-container">
                            <div className="error-content">
                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {results && (
                        <div className="results-container">
                            <div className="cripto-header">
                                <img 
                                    className="cripto-image" 
                                    src={results.image} 
                                    alt={results.name}
                                    onError={(e) => {
                                        e.target.src = 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/generic.png';
                                    }}
                                />
                                <div className="cripto-title">
                                    <h2 className="cripto-name">{results.name}</h2>
                                    <span className="symbol-badge">{results.symbol}</span>
                                </div>
                                <div className={`sentiment-badge ${getSentimentClass(results.market_sentiment)}`}>
                                    {capitalizeFirstLetter(results.market_sentiment)}
                                </div>
                            </div>

                            <div className="price-section">
                                <div className="price-main">
                                    <span className="price-value">{formatCurrency(results.price)}</span>
                                    <span className={`price-change ${results.price_change_24h >= 0 ? 'positive' : 'negative'}`}>
                                        {results.price_change_24h >= 0 ? '+' : ''}{results.price_change_24h.toFixed(2)}%
                                    </span>
                                </div>
                                <div className="price-btc">{formatNumber(results.price_btc, 8)} BTC</div>
                            </div>

                            <div className="tabs-container">
                                <button 
                                    className={`tab-btn ${activeTab === 'market' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('market')}
                                >
                                    Dados de Mercado
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('analysis')}
                                >
                                    Análise
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'community' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('community')}
                                >
                                    Comunidade
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('history')}
                                >
                                    Histórico
                                </button>
                            </div>

                            <div className="tab-content">
                                {activeTab === 'market' && (
                                    <div className="tab-pane active">
                                        <div className="info-grid">
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                                    </svg>
                                                    Capitalização de Mercado
                                                </h3>
                                                <p>{formatCurrency(results.market_cap)}</p>
                                            </div>
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                    </svg>
                                                    Volume (24h)
                                                </h3>
                                                <p>{formatCurrency(results.volume)}</p>
                                            </div>
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                                        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                                                    </svg>
                                                    Fornecimento Circulante
                                                </h3>
                                                <p>{results.circulating_supply ? `${formatNumber(results.circulating_supply)} ${results.symbol}` : 'N/A'}</p>
                                            </div>
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                    </svg>
                                                    Fornecimento Total
                                                </h3>
                                                <p>{results.total_supply ? `${formatNumber(results.total_supply)} ${results.symbol}` : 'N/A'}</p>
                                            </div>
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    Máxima Histórica (ATH)
                                                </h3>
                                                <p>{formatCurrency(results.ath)}</p>
                                            </div>
                                            <div className="info-card">
                                                <h3>
                                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                    Mínima Histórica (ATL)
                                                </h3>
                                                <p>{formatCurrency(results.atl)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'analysis' && (
                                    <div className="tab-pane active">
                                        <div className="analysis-content">
                                            <h3>
                                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                                </svg>
                                                Análise de Mercado
                                            </h3>
                                            <div className="sentiment-section">
                                                <h4>Sentimento do Mercado:</h4>
                                                <p className={`sentiment-badge ${getSentimentClass(results.market_sentiment)}`}>
                                                    {capitalizeFirstLetter(results.market_sentiment)}
                                                </p>
                                            </div>
                                            <div className="analysis-text">
                                                <h4>Análise Técnica:</h4>
                                                <p>{results.analysis || 'Análise não disponível no momento.'}</p>
                                            </div>
                                            <div className="performance-section">
                                                <h4>Desempenho:</h4>
                                                <div className="performance-grid">
                                                    <div className="perf-item">
                                                        <span>24h</span>
                                                        <span className={results.price_change_24h >= 0 ? 'positive' : 'negative'}>
                                                            {results.price_change_24h >= 0 ? '+' : ''}{results.price_change_24h.toFixed(2)}%
                                                        </span>
                                                    </div>
                                                    <div className="perf-item">
                                                        <span>7d</span>
                                                        <span className={results.price_change_7d >= 0 ? 'positive' : 'negative'}>
                                                            {results.price_change_7d ? `${results.price_change_7d >= 0 ? '+' : ''}${results.price_change_7d.toFixed(2)}%` : 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div className="perf-item">
                                                        <span>30d</span>
                                                        <span className={results.price_change_30d >= 0 ? 'positive' : 'negative'}>
                                                            {results.price_change_30d ? `${results.price_change_30d >= 0 ? '+' : ''}${results.price_change_30d.toFixed(2)}%` : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'community' && (
                                    <div className="tab-pane active">
                                        <div className="community-content">
                                            <h3>
                                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                                </svg>
                                                Dados da Comunidade
                                            </h3>
                                            <div className="community-stats">
                                                <div className="stat-item">
                                                    <svg className="icon icon-lg me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                                                    </svg>
                                                    <div>
                                                        <h4>Membros no Reddit</h4>
                                                        <p>{results.community_data?.reddit_subscribers ? formatNumber(results.community_data.reddit_subscribers) : 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div className="stat-item">
                                                    <svg className="icon icon-lg me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                    </svg>
                                                    <div>
                                                        <h4>Seguidores no Twitter</h4>
                                                        <p>{results.community_data?.twitter_followers ? formatNumber(results.community_data.twitter_followers) : 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div className="stat-item">
                                                    <svg className="icon icon-lg me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                                                    </svg>
                                                    <div>
                                                        <h4>Contribuidores no GitHub</h4>
                                                        <p>{results.community_data?.github_contributors ? formatNumber(results.community_data.github_contributors) : 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'history' && (
                                    <div className="tab-pane active">
                                        <div className="history-content">
                                            <h3>
                                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                </svg>
                                                Histórico de Preços
                                            </h3>
                                            <div className="history-stats">
                                                <div className="history-item">
                                                    <h4>Máxima (24h)</h4>
                                                    <p>{formatCurrency(results.high_24h)}</p>
                                                </div>
                                                <div className="history-item">
                                                    <h4>Mínima (24h)</h4>
                                                    <p>{formatCurrency(results.low_24h)}</p>
                                                </div>
                                                <div className="history-item">
                                                    <h4>Variação do ATH</h4>
                                                    <p className={results.ath_change >= 0 ? 'positive' : 'negative'}>
                                                        {results.ath_change >= 0 ? '+' : ''}{results.ath_change.toFixed(2)}%
                                                    </p>
                                                </div>
                                                <div className="history-item">
                                                    <h4>Variação do ATL</h4>
                                                    <p className={results.atl_change >= 0 ? 'positive' : 'negative'}>
                                                        {results.atl_change >= 0 ? '+' : ''}{results.atl_change.toFixed(2)}%
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="last-update">
                                <p>
                                    <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                </svg> Última atualização: {new Date(results.last_updated).toLocaleString('pt-BR')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Agent;
